import React, { useState, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { Button, Typogaphy } from 'vnetwork-ui'
import { AlertError, Loading } from 'vnetwork-ui'
import { ErrorResponse } from '@/types/shared'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import {
    updatePublicTicketRating,
    getPublicTicketRating,
} from '../../../Ticket/services'
import {
    canTicketBeRated,
    isRatingTimeoutExpired,
} from '../../../Ticket/types/utils'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const PublicTicketRating: React.FC = () => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [rating, setRating] = useState<number>(0)
    const [hoverRating, setHoverRating] = useState<number>(0)
    const [comment, setComment] = useState<string>('')
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
    const editorValueRef = useRef({ text: '', attachments: [] })

    const ticketIdParam = searchParams.get('ticketId')
    const ticketId = ticketIdParam ? parseInt(ticketIdParam, 10) : null
    const {
        data: ticket,
        isLoading: ticketLoading,
        error: ticketError,
    } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.TICKETS_PUBLIC,
                'public-ticket-rating',
                ticketId,
            ],
            queryFn: () => getPublicTicketRating(ticketId!),
            enabled: !!ticketId,
            retry: 1,
        },
        { showLoading: false, showError: false }
    )

    const handleEditorChange = useCallback((val: any) => {
        editorValueRef.current = val
        setComment(val.text)
    }, [])

    const handleStarClick = (star: number) => setRating(star)

    const errorHandler = (error: AxiosError<ErrorResponse>) => {
        console.error('Rating submission failed:', error)
        toast.error(t('ticket-error-rating-submission'))
    }

    const { mutateAsync: submitRatingMutation, isPending: isSubmitting } =
        useMutationWithHandler(
            {
                mutationFn: updatePublicTicketRating,
            },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS_PUBLIC,
                showSuccessToast: false,
                onError: errorHandler,
                onSuccess: () => {
                    setIsSubmitted(true)
                },
            }
        )

    const handleSubmit = async () => {
        if (rating < 1 || rating > 5) {
            console.error('Rating must be between 1 and 5')
            return
        }
        if (!ticketId) {
            toast.error(t('ticket-invalid-survey-info'))
            return
        }
        await submitRatingMutation({
            ticket_id: ticketId,
            rating,
            feedback: comment.trim() || undefined,
        })
    }

    if (!ticketId) {
        return <AlertError error={new Error(t('ticket-invalid-link'))} />
    }

    if (ticketLoading) {
        return (
            <div className="p-8 text-center">
                <Loading.CircularProgress width={24} height={24} />
                <Typogaphy className="mt-2">...</Typogaphy>
            </div>
        )
    }

    if (ticketError || !ticket?.result) {
        return <AlertError error={new Error(t('ticket-not-found'))} />
    }

    const ticketData = ticket.result
    const canRate = canTicketBeRated(
        ticketData.ticket_status || '',
        ticketData.ticket_time_repply,
        7
    )

    const isExpired = isRatingTimeoutExpired(ticketData.ticket_time_repply, 7)

    if (!canRate || isExpired) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-6 max-w-xl mx-auto">
                <div className="text-center">
                    <svg
                        className="w-16 h-16 text-red-500 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <Typogaphy className="text-lg font-semibold text-red-500 mb-2">
                        {t('ticket-survey-expired')}
                    </Typogaphy>
                    <Typogaphy className="text-red-500 mb-4">
                        {t('ticket-rating-time-ended')}
                    </Typogaphy>
                </div>
            </div>
        )
    }

    if (
        ticketData.ticket_customer_rating &&
        parseInt(ticketData.ticket_customer_rating) > 0
    ) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-6 max-w-xl mx-auto">
                <div className="text-center">
                    <svg
                        className="w-16 h-16 text-red-500 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <Typogaphy className="text-lg font-semibold text-red-500 mb-2">
                        {t('ticket-survey-expired')}
                    </Typogaphy>
                    <Typogaphy className="text-red-500 mb-4">
                        {t('ticket-rating-time-ended')}
                    </Typogaphy>
                </div>
            </div>
        )
    }

    const renderStars = () => {
        return Array.from({ length: 5 }, (_, index) => {
            const starNumber = index + 1
            const isActive = starNumber <= (hoverRating || rating)

            return (
                <button
                    key={starNumber}
                    onClick={() => handleStarClick(starNumber)}
                    onMouseEnter={() => setHoverRating(starNumber)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-all duration-200 hover:scale-110"
                    disabled={isSubmitting}
                >
                    <svg
                        className={`w-8 h-8 transition-colors duration-200 ${
                            isActive
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300 fill-current hover:text-yellow-200'
                        }`}
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                </button>
            )
        })
    }

    return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-7 max-w-2xl mx-auto">
            <div className="text-center">
                {!isSubmitted ? (
                    <>
                        <Typogaphy
                            size="large"
                            className="font-semibold text-blue-800 mb-2"
                        >
                            {t('ticket-satisfaction-rating')}
                        </Typogaphy>

                        <div className="mb-4">
                            <Typogaphy size="medium" className="mb-2">
                                {t('ticket-thank-you-message')}
                            </Typogaphy>
                            <div className="grid grid-cols-1 lg:pl-[40px] pl-4 mb-2 space-y-2 text-left">
                                <Typogaphy size="small">
                                    {t('ticket-assigned-employee')} {''}
                                    {ticketData.employee_name || ''}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-creation-time')} {''}
                                    {ticketData.ticket_requested_at
                                        ? dayjs(
                                              ticketData.ticket_requested_at
                                          ).format('DD/MM/YYYY')
                                        : ''}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-rating-customer')} {''}
                                    {ticketData.customer_name || 'N/A'}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-product-service-support')} {''}
                                    {ticketData.product_group_name ||
                                        ticketData.service_name ||
                                        'N/A'}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-rating-status')} {''}
                                    {ticketData.ticket_status === 'closed'
                                        ? t('ticket-status-completed')
                                        : ticketData.ticket_status}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-completion-date')} {''}
                                    {ticketData.ticket_time_release
                                        ? dayjs(
                                              ticketData.ticket_time_release
                                          ).format('DD/MM/YYYY')
                                        : ''}
                                </Typogaphy>
                                <Typogaphy size="small">
                                    {t('ticket-rating-ticket-type')} {''}
                                    {ticketData.ticket_type_title || 'N/A'}
                                </Typogaphy>
                            </div>
                            <Typogaphy size="small" className="mt-2">
                                {t('ticket-rate-experience', { ticketId })}
                            </Typogaphy>
                        </div>

                        <div className="flex justify-center gap-2 mb-4">
                            {renderStars()}
                        </div>

                        {rating >= 1 && (
                            <div className="mb-4">
                                <Typogaphy className="text-sm text-gray-700 mb-2 text-left">
                                    {t('ticket-share-reason')}
                                </Typogaphy>
                                <div className="border border-gray-300 rounded">
                                    <TextEditorCustomize
                                        value={editorValueRef.current}
                                        onChange={handleEditorChange}
                                        placeholder={t('ticket-enter-opinion')}
                                        disabledAttachments={true}
                                    />
                                </div>
                            </div>
                        )}

                        {rating >= 1 && (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className=" text-white px-6 py-2"
                            >
                                {isSubmitting
                                    ? t('ticket-submitting')
                                    : t('ticket-confirm-rating')}
                            </Button>
                        )}
                    </>
                ) : (
                    <div className="text-center py-8">
                        <Typogaphy className="text-lg font-semibold text-green-700 mb-2">
                            {t('ticket-thank-you-rating')}
                        </Typogaphy>
                        <Typogaphy className="text-sm text-gray-700">
                            {t('ticket-improve-service')}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PublicTicketRating
